import { useState } from 'react'
import { blissZipServiceStatus } from '../../utils/blissServiceArea'
import BlissFunnelFrame from './BlissFunnelFrame'
import BlissZipFeedback from './BlissZipFeedback'

export type StudioContact = { name?: string; email?: string; phone?: string; zip?: string }
export const completeStudioContact = (c: StudioContact) =>
  !!c.name?.trim() &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email ?? '') &&
  ['full_service', 'design_consult_only'].includes(blissZipServiceStatus(c.zip ?? ''))

export default function StudioContactStart({
  verificationCall = false,
  initial,
  onContinue,
  continueLabel = 'Continue to Bunkroom Design Studio →',
  readyForPackages = false,
}: {
  verificationCall?: boolean
  initial: StudioContact
  readyForPackages?: boolean
  continueLabel?: string
  onContinue: (contact: StudioContact) => void | Promise<void>
}) {
  const [contact, setContact] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const valid = completeStudioContact(contact) && (!verificationCall || (contact.phone?.replace(/\D/g, '').length ?? 0) >= 10)
  return (
    <BlissFunnelFrame>
      <div className="bliss-funnel-contact">
        <div className="bliss-funnel-copy">
          <h1>Your contact details</h1>
          <p>
            {verificationCall ? 'We’ll verify your project details and contact information in a brief call, then send your quote.' : readyForPackages
              ? 'Your room is saved. Add your contact details to keep your room and proposal together, then review your packages.'
              : 'Enter your details so we can save your project and follow up — then open Bunkroom Design Studio.'}
          </p>
          {verificationCall ? <p>A representative will be assigned to follow up after your quote is sent.</p> : <ul>
            <li>
              {readyForPackages
                ? 'Check delivery and installation for your jobsite before choosing your package.'
                : 'Check delivery and installation for your jobsite, then start arranging your room.'}
            </li>
            <li>
              Your room and contact details stay together, so you can return to your proposal later.
            </li>
            <li>
              Choose your package, review the terms and sign before paying a deposit. Nothing is
              charged now.
            </li>
          </ul>}
        </div>
        <form
          className="bliss-funnel-form"
          onSubmit={async e => {
            e.preventDefault()
            if (busy || !valid) return
            setBusy(true)
            setError('')
            try {
              await onContinue(contact)
            } catch (err) {
              setError(
                err instanceof Error
                  ? err.message
                  : 'Could not save your details. Please try again.'
              )
            } finally {
              setBusy(false)
            }
          }}
        >
          <label>
            Full Name
            <input
              required
              autoComplete="name"
              placeholder="Your name"
              maxLength={100}
              value={contact.name ?? ''}
              onChange={e => setContact({ ...contact, name: e.target.value })}
            />
          </label>
          <label>
            Email
            <input
              required
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              maxLength={100}
              value={contact.email ?? ''}
              onChange={e => setContact({ ...contact, email: e.target.value })}
            />
          </label>
          <label>
            {verificationCall ? "Phone" : "Phone (optional)"}
            <input
              required={verificationCall}
              type="tel"
              autoComplete="tel"
              placeholder="(555) 555-5555"
              maxLength={30}
              value={contact.phone ?? ''}
              onChange={e => setContact({ ...contact, phone: e.target.value })}
            />
          </label>
          <label>
            Jobsite ZIP
            <input
              required
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="Five-digit ZIP"
              pattern="[0-9]{5}"
              maxLength={5}
              value={contact.zip ?? ''}
              onChange={e =>
                setContact({ ...contact, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })
              }
            />
          </label>
          <BlissZipFeedback zip={contact.zip ?? ''} showPricing={!verificationCall} />
          {error && <p role="alert">{error}</p>}
          <button disabled={busy || !valid}>
            {busy ? 'Saving your details…' : continueLabel}
          </button>
        </form>
      </div>
    </BlissFunnelFrame>
  )
}
